import BaseService from '../BaseService';
import type { FilterModel } from 'ag-grid-community';
import { convertFilterModelToBackend } from '@/queries/ssrm/convertFilterModelToBackend';
import type { SsrmMetricsResponse, SsrmRequestBody, SsrmColumnVO, SsrmFilterModel } from './CncSummaryMetricsService';

export interface AgaMetricsDateRange {
  start: Date;
  end: Date;
  label: string;
}

export interface FundingRequestMetricsRow {
  id?: number;
  business?: string;
  createdAt?: string;
  mainCurrency?: string;
  requestedAmount?: number;
  clientName?: string;
  childCount?: number;
}

export interface FileSubmissionMetricsRow {
  id?: number;
  createdAt?: string;
  category?: string;
  clientName?: string;
  childCount?: number;
}

export const RESI_METRICS_BUSINESS = 'RESI';
const SSRM_PAGE_SIZE = 500;
const FETCH_CONCURRENCY = 5;

function toDateFilterString(date: Date, useEndOfDay = false): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return useEndOfDay ? `${year}-${month}-${day} 23:59:59` : `${year}-${month}-${day} 00:00:00`;
}

function buildSetFilter(field: string, values: readonly string[]): Record<string, unknown> {
  return {
    [field]: {
      filterType: 'set',
      values: [...values],
    },
  };
}

function buildDateRangeFilter(field: string, start: Date, end: Date): Record<string, unknown> {
  return {
    [field]: {
      filterType: 'date',
      type: 'inRange',
      dateFrom: toDateFilterString(start),
      dateTo: toDateFilterString(end, true),
    },
  };
}

function mergeFilterModels(...models: Record<string, unknown>[]): Record<string, unknown> {
  return models.reduce<Record<string, unknown>>((acc, model) => ({ ...acc, ...model }), {});
}

async function fetchAllSsrmRows<TRow>(
  fetchPage: (startRow: number, endRow: number) => Promise<SsrmMetricsResponse<TRow>>,
): Promise<TRow[]> {
  const first = await fetchPage(0, SSRM_PAGE_SIZE);
  const firstRows = first.rowData ?? [];

  if (first.rowCount == null) {
    if (firstRows.length < SSRM_PAGE_SIZE) {
      return firstRows;
    }
    throw new Error(
      `fetchAllSsrmRows: SSRM response missing rowCount on a full page (${firstRows.length} rows); cannot determine total.`,
    );
  }

  const total = first.rowCount;

  if (total <= SSRM_PAGE_SIZE) {
    return firstRows;
  }

  const ranges: Array<[number, number]> = [];
  for (let start = SSRM_PAGE_SIZE; start < total; start += SSRM_PAGE_SIZE) {
    ranges.push([start, Math.min(start + SSRM_PAGE_SIZE, total)]);
  }

  const results: TRow[][] = [firstRows];
  for (let i = 0; i < ranges.length; i += FETCH_CONCURRENCY) {
    const chunk = ranges.slice(i, i + FETCH_CONCURRENCY);
    const pages = await Promise.all(chunk.map(([start, end]) => fetchPage(start, end)));
    results.push(...pages.map(p => p.rowData ?? []));
  }

  return results.flat();
}

async function fetchFlatGroupedSsrmRows<TRow>(
  fetchPage: (startRow: number, endRow: number) => Promise<SsrmMetricsResponse<TRow>>,
): Promise<TRow[]> {
  const rows = await fetchAllSsrmRows(fetchPage);
  if (rows.length > 0 && rows.every(row => (row as { childCount?: number }).childCount === undefined)) {
    throw new Error(
      'FLAT grouping response has no childCount on any row; the backend does not appear to support groupingMode=FLAT.',
    );
  }
  return rows;
}

function buildSsrmRequest(
  filterModel: SsrmFilterModel,
  startRow: number,
  endRow: number,
  sortColId: string,
  rowGroupCols: SsrmColumnVO[] = [],
  valueCols: SsrmColumnVO[] = [],
  groupingMode?: 'FLAT' | 'HIERARCHICAL',
  havingModel?: SsrmFilterModel,
): SsrmRequestBody {
  return {
    startRow,
    endRow,
    rowGroupCols,
    groupKeys: [],
    valueCols,
    pivotCols: [],
    pivotMode: false,
    filterModel,
    sortModel: [{ colId: sortColId, sort: 'asc' }],
    convertedFilterModel: convertFilterModelToBackend(filterModel as FilterModel),
    ...(groupingMode !== undefined && { groupingMode }),
    ...(havingModel !== undefined && { havingModel }),
  };
}

class ResiAgaMetricsService extends BaseService {
  constructor() {
    super('/api/funding-service');
  }

  getFundingRequestSsrmPage = (request: SsrmRequestBody): Promise<SsrmMetricsResponse<FundingRequestMetricsRow>> =>
    this.post<SsrmRequestBody, SsrmMetricsResponse<FundingRequestMetricsRow>>({
      path: 'fundingrequests/ssrm',
      body: request,
    });

  getFileSubmissionSsrmPage = (request: SsrmRequestBody): Promise<SsrmMetricsResponse<FileSubmissionMetricsRow>> =>
    this.post<SsrmRequestBody, SsrmMetricsResponse<FileSubmissionMetricsRow>>({
      path: 'filesubmissions/ssrm',
      body: request,
    });

  fetchFundingRequestRows = (
    filterModel: SsrmFilterModel,
    rowGroupCols: SsrmColumnVO[] = [],
    valueCols: SsrmColumnVO[] = [],
  ): Promise<FundingRequestMetricsRow[]> =>
    fetchAllSsrmRows((startRow, endRow) =>
      this.getFundingRequestSsrmPage(
        buildSsrmRequest(filterModel, startRow, endRow, 'createdAt', rowGroupCols, valueCols),
      ),
    );

  fetchGroupedFundingRequestRows = (
    filterModel: SsrmFilterModel,
    groupCols: SsrmColumnVO[],
    valueCols: SsrmColumnVO[],
    havingModel?: SsrmFilterModel,
  ): Promise<FundingRequestMetricsRow[]> =>
    fetchFlatGroupedSsrmRows((startRow, endRow) =>
      this.getFundingRequestSsrmPage(
        buildSsrmRequest(filterModel, startRow, endRow, 'createdAt', groupCols, valueCols, 'FLAT', havingModel),
      ),
    );

  fetchFileSubmissionRows = (
    filterModel: SsrmFilterModel,
    rowGroupCols: SsrmColumnVO[] = [],
  ): Promise<FileSubmissionMetricsRow[]> =>
    fetchAllSsrmRows((startRow, endRow) =>
      this.getFileSubmissionSsrmPage(buildSsrmRequest(filterModel, startRow, endRow, 'createdAt', rowGroupCols)),
    );

  fetchGroupedFileSubmissionRows = (
    filterModel: SsrmFilterModel,
    groupCols: SsrmColumnVO[],
    havingModel?: SsrmFilterModel,
  ): Promise<FileSubmissionMetricsRow[]> =>
    fetchFlatGroupedSsrmRows((startRow, endRow) =>
      this.getFileSubmissionSsrmPage(
        buildSsrmRequest(filterModel, startRow, endRow, 'createdAt', groupCols, [], 'FLAT', havingModel),
      ),
    );
}

export function buildResiFundingRequestDateFilter(range: AgaMetricsDateRange): Record<string, unknown> {
  return mergeFilterModels(
    buildSetFilter('business', [RESI_METRICS_BUSINESS]),
    buildDateRangeFilter('createdAt', range.start, range.end),
  );
}

export function buildResiFileSubmissionDateFilter(range: AgaMetricsDateRange): Record<string, unknown> {
  return buildDateRangeFilter('createdAt', range.start, range.end);
}

export default ResiAgaMetricsService;
